"use client";

type FrameCallback = (time: number, dt: number) => void;

interface Subscription {
    id: string;
    callback: FrameCallback;
    element: HTMLElement | null;
    priority: 'critical' | 'normal' | 'idle';
}

class FrameScheduler {
    private subs = new Map<string, Subscription>();
    private rafId = 0;
    private prevTime = 0;
    private isRunning = false;
    private isTabVisible = true;
    private visibleElements = new Set<string>();
    private observer: IntersectionObserver | null = null;

    constructor() {
        if (typeof window === 'undefined') return;

        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = (entry.target as HTMLElement).dataset.frameId;
                    if (!id) continue;

                    if (entry.isIntersecting) {
                        this.visibleElements.add(id);
                    } else {
                        this.visibleElements.delete(id);
                    }
                }
                this.reconcile();
            },
            { threshold: 0 }
        );

        document.addEventListener('visibilitychange', () => {
            this.isTabVisible = !document.hidden;
            this.reconcile();
        });
    }

    public subscribe(
        id: string,
        callback: FrameCallback,
        element?: HTMLElement | null,
        priority: 'critical' | 'normal' | 'idle' = 'normal'
    ): () => void {
        this.subs.set(id, { id, callback, element: element ?? null, priority });

        if (element && this.observer) {
            element.dataset.frameId = id;
            this.observer.observe(element);
        } else {
            // No element means always active when tab is visible
            this.visibleElements.add(id);
        }

        this.reconcile();

        return () => this.unsubscribe(id);
    }

    public unsubscribe(id: string): void {
        const sub = this.subs.get(id);
        if (sub?.element && this.observer) {
            this.observer.unobserve(sub.element);
        }
        this.subs.delete(id);
        this.visibleElements.delete(id);
        this.reconcile();
    }

    private reconcile(): void {
        const shouldRun = this.isTabVisible && (this.visibleElements.size > 0 || this.subs.size > 0);
        
        // Active subscribers that are currently visible
        let activeCount = 0;
        for (const [id, sub] of this.subs) {
            if (!sub.element || this.visibleElements.has(id)) {
                activeCount++;
            }
        }

        if (this.isTabVisible && activeCount > 0) {
            if (!this.isRunning) this.start();
        } else {
            if (this.isRunning) this.stop();
        }
    }

    private start(): void {
        this.isRunning = true;
        this.prevTime = performance.now();
        this.rafId = requestAnimationFrame(this.tick);
    }

    private stop(): void {
        this.isRunning = false;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = 0;
        }
    }

    private tick = (time: number): void => {
        if (!this.isRunning) return;

        const dt = Math.min((time - this.prevTime) / 1000, 0.05);
        this.prevTime = time;

        for (const [id, sub] of this.subs) {
            // Skip elements that are outside the viewport
            if (sub.element && !this.visibleElements.has(id)) continue;
            try {
                sub.callback(time, dt);
            } catch (err) {
                console.error(`[FrameScheduler] Error in task '${id}':`, err);
            }
        }

        this.rafId = requestAnimationFrame(this.tick);
    };
}

export const frameScheduler = new FrameScheduler();
